import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[420px] md:min-h-[600px] flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Serene%20luxury%20wellness%20spa%20interior%20with%20soft%20natural%20green%20plants%20and%20warm%20golden%20ambient%20lighting%2C%20modern%20minimalist%20design%2C%20cream%20wood%20tones%2C%20peaceful%20relaxing%20atmosphere%2C%20cinematic%20photography%20with%20shallow%20depth%20of%20field%2C%20elegant%20healthcare%20environment&width=1600&height=900&seq=hero-wellness-bg&orientation=landscape"
          alt="Xanh Wellness spa interior"
          className="w-full h-full object-cover object-top"
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-900/50 to-primary-950/70" />
      </div>

      {/* Content */}
      <div
        className={`relative z-10 w-full text-center px-4 md:px-8 py-20 md:py-28 transition-all duration-1000 ${
          visible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-8"
        }`}
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-500">
            <i className="ri-leaf-line text-xl md:text-2xl text-background-50" />
          </div>
          <span className="font-label text-xs md:text-sm tracking-[0.2em] uppercase text-accent-300 font-medium">
            Chăm sóc từ tâm — Khỏe đẹp tự nhiên
          </span>
        </div>

        <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-4 max-w-4xl mx-auto">
          Trải nghiệm công nghệ cao
          <br className="hidden md:block" />
          <span className="text-accent-400">nâng tầm sức khỏe</span>
        </h1>

        <p className="text-sm md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed font-body">
          Cabin spa thông minh — hiện đại, hiệu quả, an toàn. Giảm cân, giảm mỡ, thư giãn cơ thể chỉ trong 40 phút trải nghiệm.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
          <Link
            to="/booking"
            className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm md:text-base font-semibold whitespace-nowrap rounded-md transition-colors"
          >
            Đặt lịch trải nghiệm
          </Link>
          <Link
            to="/services"
            className="px-8 py-3.5 bg-background-50/15 hover:bg-background-50/25 text-background-50 border border-background-50/30 font-label text-sm md:text-base font-medium whitespace-nowrap rounded-md transition-colors backdrop-blur-sm"
          >
            Khám phá dịch vụ
          </Link>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mt-10 md:mt-14">
          <div className="text-center">
            <div className="font-display text-2xl md:text-3xl font-bold text-accent-400">
              40
            </div>
            <div className="text-xs md:text-sm text-background-300 font-label">
              Phút trải nghiệm
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-background-50/20" />
          <div className="text-center">
            <div className="font-display text-2xl md:text-3xl font-bold text-accent-400">
              1000+
            </div>
            <div className="text-xs md:text-sm text-background-300 font-label">
              Khách hàng hài lòng
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-background-50/20" />
          <div className="text-center">
            <div className="font-display text-2xl md:text-3xl font-bold text-accent-400">
              4.9/5
            </div>
            <div className="text-xs md:text-sm text-background-300 font-label">
              Đánh giá từ khách hàng
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}